/**
 * Recruiter candidate actions widget:
 *   - "Request vault access" — opens a small dialog to send a request to a
 *     candidate by email; candidate gets in-app notification + can approve.
 *   - "Send AI overview to job" — pushes the candidate's approved AI profile
 *     to a specific requisition; notifies every collaborator.
 *
 * Designed to mount inside the recruiter cockpit / candidate row / requisition
 * detail. Standalone, props-driven, no router required.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Inbox, Send, KeyRound, X, Check, Loader2 } from 'lucide-react';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTIONS = [
  { key: 'employment', label: 'Employment history' },
  { key: 'ai_profile', label: 'Approved AI profile' },
  { key: 'compliance_readiness', label: 'Compliance pack readiness' },
  { key: 'documents', label: 'Documents' },
  { key: 'pii', label: 'PII (name/contact/address)' },
];

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-[var(--vh-bg-surface)] rounded-2xl shadow-2xl border border-[var(--vh-border)] w-full max-w-lg p-6" data-testid={testid}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display text-xl text-[var(--vh-text-primary)]">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[var(--vh-bg-muted)]">
            <X className="w-5 h-5 text-[var(--vh-text-muted)]" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function RequestVaultAccessDialog({ initialEmail, initialRequisitionId, onClose, onSuccess }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [requisitionId] = useState(initialRequisitionId || '');
  const [message, setMessage] = useState('');
  const [sections, setSections] = useState(['employment', 'ai_profile']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggle = (key) => setSections((s) => (s.includes(key) ? s.filter(x => x !== key) : [...s, key]));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/cockpit/vault-access-requests`, {
        candidate_email: email,
        requisition_id: requisitionId || null,
        message: message || null,
        requested_sections: sections,
      });
      onSuccess?.();
      onClose();
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Could not send request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Request vault access" onClose={onClose} testid="request-vault-access-dialog">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1 text-[var(--vh-text-secondary)]">Candidate email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)]"
            required
            data-testid="request-vault-access-email"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-[var(--vh-text-secondary)]">Why are you asking?</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)]"
            placeholder="E.g. Reviewing your application for Senior Compliance Manager at our bank."
            data-testid="request-vault-access-message"
          />
        </div>
        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium text-[var(--vh-text-secondary)] mb-1">Sections requested</legend>
          {SECTIONS.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={sections.includes(key)}
                onChange={() => toggle(key)}
                className="h-4 w-4 rounded border-[var(--vh-border)] text-[var(--vh-brand-primary)]"
                data-testid={`request-vault-access-section-${key}`}
              />
              {label}
            </label>
          ))}
        </fieldset>
        {error && <p className="text-sm text-red-700" data-testid="request-vault-access-error">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[var(--vh-border)] text-sm">Cancel</button>
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2 rounded-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-sm font-medium disabled:opacity-60"
            data-testid="request-vault-access-submit"
          >
            {loading ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ShareAIOverviewDialog({ candidateUserId, candidateName, onClose, onSuccess }) {
  const [requisitions, setRequisitions] = useState([]);
  const [reqId, setReqId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingReqs, setLoadingReqs] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cockpit/requisitions`);
        if (alive) {
          setRequisitions(Array.isArray(data) ? data : []);
          if (data && data.length > 0) setReqId(data[0].id);
        }
      } catch (e) {
        // Recruiter may have no requisitions yet, or be unauthorised on this surface.
        // We render an empty-state in the UI rather than crashing the dialog.
        logger.warn('share-ai-overview requisitions load failed', e?.response?.status);
      } finally { if (alive) setLoadingReqs(false); }
    })();
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/cockpit/share-ai-overview`, {
        candidate_user_id: candidateUserId,
        requisition_id: reqId,
        notes: notes || null,
      });
      onSuccess?.();
      onClose();
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Could not share AI overview.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Send AI overview to a job" onClose={onClose} testid="share-ai-overview-dialog">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-[var(--vh-text-secondary)]">
          Pushing <strong className="text-[var(--vh-text-primary)]">{candidateName || 'this candidate'}</strong>'s approved AI profile to a requisition will notify every collaborator (hiring manager + panel) in-app.
        </p>
        <div>
          <label className="block text-sm font-medium mb-1 text-[var(--vh-text-secondary)]">Requisition</label>
          {loadingReqs ? (
            <p className="text-sm text-[var(--vh-text-muted)]">Loading requisitions…</p>
          ) : requisitions.length === 0 ? (
            <p className="text-sm text-[var(--vh-text-muted)]">No requisitions in your Vault Hub yet. Create one first.</p>
          ) : (
            <select
              value={reqId}
              onChange={(e) => setReqId(e.target.value)}
              required
              className="w-full px-3 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)]"
              data-testid="share-ai-overview-req"
            >
              {requisitions.map(r => (
                <option key={r.id} value={r.id}>{r.title || r.role || r.id.slice(0, 8)}</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 text-[var(--vh-text-secondary)]">Notes for the team (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-[var(--vh-border)] rounded-lg bg-[var(--vh-bg-base)]"
            placeholder="Why this candidate, what to look at first…"
            data-testid="share-ai-overview-notes"
          />
        </div>
        {error && <p className="text-sm text-red-700" data-testid="share-ai-overview-error">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[var(--vh-border)] text-sm">Cancel</button>
          <button
            type="submit"
            disabled={loading || !reqId}
            className="px-5 py-2 rounded-full bg-[var(--vh-accent)] hover:bg-[var(--vh-accent-hover)] text-white text-sm font-medium disabled:opacity-60"
            data-testid="share-ai-overview-submit"
          >
            {loading ? 'Sending…' : 'Send to job'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function RecruiterCandidateActions({
  candidateEmail,
  candidateUserId,
  candidateName,
  requisitionId,
  compact = false,
}) {
  const [showReq, setShowReq] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [flash, setFlash] = useState('');

  const buttons = useMemo(() => {
    const list = [
      {
        key: 'request',
        icon: <KeyRound className="w-4 h-4" />,
        label: 'Request vault access',
        onClick: () => setShowReq(true),
        testid: 'recruiter-action-request-vault',
        tone: 'primary',
      },
    ];
    if (candidateUserId) {
      list.push({
        key: 'share',
        icon: <Send className="w-4 h-4" />,
        label: 'Send AI overview to job',
        onClick: () => setShowShare(true),
        testid: 'recruiter-action-share-ai',
        tone: 'accent',
      });
    }
    return list;
  }, [candidateUserId]);

  return (
    <div className={`flex flex-wrap gap-2 items-center ${compact ? '' : 'p-3 rounded-xl bg-[var(--vh-bg-muted)] border border-[var(--vh-border)]'}`} data-testid="recruiter-candidate-actions">
      {!compact && (
        <div className="mr-2 inline-flex items-center gap-2 text-xs text-[var(--vh-text-secondary)] mr-auto">
          <Inbox className="w-4 h-4" />
          Candidate actions
        </div>
      )}
      {buttons.map(b => (
        <button
          key={b.key}
          type="button"
          onClick={b.onClick}
          disabled={b.disabled}
          data-testid={b.testid}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
            b.tone === 'accent'
              ? 'bg-[var(--vh-accent)] hover:bg-[var(--vh-accent-hover)] text-white'
              : 'bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title={b.disabled ? 'Need an approved AI profile + candidate account' : ''}
        >
          {b.icon}
          {b.label}
        </button>
      ))}
      {flash && (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
          <Check className="w-3.5 h-3.5" />{flash}
        </span>
      )}

      {showReq && (
        <RequestVaultAccessDialog
          initialEmail={candidateEmail}
          initialRequisitionId={requisitionId}
          onClose={() => setShowReq(false)}
          onSuccess={() => { setFlash('Request sent'); setTimeout(() => setFlash(''), 2500); }}
        />
      )}
      {showShare && (
        <ShareAIOverviewDialog
          candidateUserId={candidateUserId}
          candidateName={candidateName}
          onClose={() => setShowShare(false)}
          onSuccess={() => { setFlash('AI overview shared'); setTimeout(() => setFlash(''), 2500); }}
        />
      )}
    </div>
  );
}
